import React, { FormEvent, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Download,
  GraduationCap,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  FileInput,
  Mic2,
  Plus,
  QrCode,
  Search,
  Ticket,
  Upload,
  Users,
  X,
} from "lucide-react";
import {
  ActivityLog,
  Attendee,
  AttendeeStatus,
  EventOperationsState,
  EventSession,
  PaymentStatus,
  ScholarshipStatus,
  Speaker,
  Sponsor,
  TicketType,
} from "../types";
import {
  calculateCouponDiscount,
  calculateEventFinancialBreakdowns,
  calculateEventOverview,
  createAttendeeCode,
  createEventEntityId,
  getOrderNetRevenue,
  mergeImportedAttendees,
  resolveTicketType,
} from "../lib/eventOperations";
import { EventAttendeeImportModal } from "./events/EventAttendeeImportModal";
import { EventCheckInModal } from "./events/EventCheckInModal";
import { EventCommunicationsPanel } from "./events/EventCommunicationsPanel";
import { EventCouponManager } from "./events/EventCouponManager";
import { EventRegistrationFormBuilder } from "./events/EventRegistrationFormBuilder";
import { EventRegistrationPortalModal } from "./events/EventRegistrationPortalModal";

type InternalView =
  | "overview"
  | "convocation"
  | "attendees"
  | "tickets"
  | "scholarships"
  | "speakers"
  | "agenda"
  | "communications";
type ExternalView = "canvas" | "crm" | "sponsors" | "studio" | "metrics";

interface EventOperationsSectionProps {
  state: EventOperationsState;
  onChange: (state: EventOperationsState) => void;
  sponsors: Sponsor[];
  onNavigate: (view: ExternalView) => void;
}

const ATTENDEE_LABELS: Record<AttendeeStatus, string> = {
  invited: "Invitado",
  started: "Inició inscripción",
  pending: "Pendiente",
  under_review: "En revisión",
  approved: "Aprobado",
  payment_pending: "Pago pendiente",
  registered: "Inscripto",
  checked_in: "Checked-in",
  no_show: "No-show",
  cancelled: "Cancelado",
};

const PAYMENT_LABELS: Record<PaymentStatus, string> = {
  pending: "Pendiente",
  processing: "Procesando",
  paid: "Pagado",
  failed: "Fallido",
  partially_refunded: "Reembolso parcial",
  refunded: "Reembolsado",
  cancelled: "Cancelado",
};

const SCHOLARSHIP_LABELS: Record<ScholarshipStatus, string> = {
  requested: "Solicitud",
  under_review: "En revisión",
  approved: "Aprobada",
  partial: "Parcial",
  rejected: "Rechazada",
  code_issued: "Código emitido",
  used: "Utilizada",
};

const statusTone = (status: string) => {
  if (
    [
      "paid",
      "registered",
      "checked_in",
      "approved",
      "published",
      "complete",
      "used",
      "code_issued",
    ].includes(status)
  ) {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (
    [
      "pending",
      "processing",
      "payment_pending",
      "under_review",
      "requested",
      "info_pending",
    ].includes(status)
  ) {
    return "bg-amber-50 text-amber-800 border-amber-200";
  }
  if (["failed", "cancelled", "rejected", "no_show"].includes(status)) {
    return "bg-rose-50 text-rose-800 border-rose-200";
  }
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const formatMoney = (value: number, currency: string = "USD") =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);

const ModalShell: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, onClose, children }) => (
  <div
    className="fixed inset-0 z-[80] bg-slate-950/55 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-2xl max-h-[92dvh] overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-black text-slate-950">{title}</h2>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-100 flex items-center justify-center"
          aria-label="Cerrar"
        >
          <X size={17} />
        </button>
      </div>
      <div className="overflow-y-auto p-5">{children}</div>
    </div>
  </div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <label className="block min-w-0">
    <span className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
      {label}
      {required ? " *" : ""}
    </span>
    {children}
  </label>
);

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  detail: string;
}> = ({ icon, title, detail }) => (
  <div className="bg-white border border-dashed border-slate-300 rounded-2xl py-12 px-5 text-center">
    <span className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-400 mx-auto flex items-center justify-center">
      {icon}
    </span>
    <p className="font-display font-black text-slate-800 mt-3">{title}</p>
    <p className="text-xs text-slate-500 mt-1">{detail}</p>
  </div>
);

const inputClass =
  "w-full h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-500";

export const EventOperationsSection: React.FC<EventOperationsSectionProps> = ({
  state,
  onChange,
  sponsors,
  onNavigate,
}) => {
  const [activeView, setActiveView] = useState<InternalView>("overview");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | AttendeeStatus>(
    "all",
  );
  const [countryFilter, setCountryFilter] = useState("all");
  const [ticketFilter, setTicketFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState<"all" | PaymentStatus>(
    "all",
  );
  const [sortBy, setSortBy] = useState<
    "registered_desc" | "name_asc" | "country_asc"
  >("registered_desc");
  const [attendeeModal, setAttendeeModal] = useState<Attendee | "new" | null>(
    null,
  );
  const [ticketModal, setTicketModal] = useState<TicketType | "new" | null>(
    null,
  );
  const [speakerModal, setSpeakerModal] = useState<Speaker | "new" | null>(
    null,
  );
  const [sessionModal, setSessionModal] = useState<EventSession | "new" | null>(
    null,
  );
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [formBuilderOpen, setFormBuilderOpen] = useState(false);
  const [registrationPortalOpen, setRegistrationPortalOpen] = useState(false);
  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<string[]>([]);
  const [professionFilter, setProfessionFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [attendeeSaveError, setAttendeeSaveError] = useState("");

  const metrics = useMemo(() => calculateEventOverview(state), [state]);
  const financialBreakdowns = useMemo(
    () => calculateEventFinancialBreakdowns(state),
    [state],
  );
  const countries = useMemo(
    () =>
      Array.from(new Set(state.attendees.map((item) => item.country))).sort(),
    [state.attendees],
  );
  const availableTicketIds = useMemo(
    () =>
      new Set(
        state.ticketTypes
          .filter(
            (ticket) =>
              resolveTicketType(state.ticketTypes, ticket.id)?.id === ticket.id,
          )
          .map((ticket) => ticket.id),
      ),
    [state.ticketTypes],
  );
  const professions = useMemo(
    () =>
      Array.from(new Set(state.attendees.map((item) => item.profession)))
        .filter(Boolean)
        .sort(),
    [state.attendees],
  );
  const sources = useMemo(
    () =>
      Array.from(
        new Set(state.attendees.map((item) => item.attribution.source)),
      )
        .filter(Boolean)
        .sort(),
    [state.attendees],
  );
  const sponsorMap = useMemo(
    () => new Map(sponsors.map((sponsor) => [sponsor.id, sponsor.companyName])),
    [sponsors],
  );
  const speakerMap = useMemo(
    () =>
      new Map(
        state.speakers.map((speaker) => [
          speaker.id,
          `${speaker.firstName} ${speaker.lastName}`,
        ]),
      ),
    [state.speakers],
  );
  const roomMap = useMemo(
    () => new Map(state.rooms.map((room) => [room.id, room.name])),
    [state.rooms],
  );
  const trackMap = useMemo(
    () => new Map(state.tracks.map((track) => [track.id, track])),
    [state.tracks],
  );

  const commit = (updates: Partial<EventOperationsState>) =>
    onChange({ ...state, ...updates, updatedAt: new Date().toISOString() });
  const createActivityLog = (
    entityType: ActivityLog["entityType"],
    entityId: string,
    action: string,
    description: string,
    createdAt = new Date().toISOString(),
  ): ActivityLog => ({
    id: createEventEntityId("log"),
    eventId: state.event.id,
    entityType,
    entityId,
    action,
    description,
    createdAt,
  });

  const filteredAttendees = useMemo(() => {
    const query = search.trim().toLowerCase();
    return state.attendees
      .filter(
        (attendee) =>
          !query ||
          [
            attendee.firstName,
            attendee.lastName,
            attendee.email,
            attendee.institution,
            attendee.specialty,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query)),
      )
      .filter(
        (attendee) =>
          statusFilter === "all" ||
          attendee.registrationStatus === statusFilter,
      )
      .filter(
        (attendee) =>
          countryFilter === "all" || attendee.country === countryFilter,
      )
      .filter(
        (attendee) =>
          ticketFilter === "all" || attendee.ticketTypeId === ticketFilter,
      )
      .filter(
        (attendee) =>
          paymentFilter === "all" || attendee.paymentStatus === paymentFilter,
      )
      .filter(
        (attendee) =>
          professionFilter === "all" ||
          attendee.profession === professionFilter,
      )
      .filter(
        (attendee) =>
          sourceFilter === "all" ||
          attendee.attribution.source === sourceFilter,
      )
      .sort((a, b) => {
        if (sortBy === "name_asc")
          return `${a.lastName} ${a.firstName}`.localeCompare(
            `${b.lastName} ${b.firstName}`,
          );
        if (sortBy === "country_asc") return a.country.localeCompare(b.country);
        return b.registeredAt.localeCompare(a.registeredAt);
      });
  }, [
    state.attendees,
    search,
    statusFilter,
    countryFilter,
    ticketFilter,
    paymentFilter,
    professionFilter,
    sourceFilter,
    sortBy,
  ]);

  const sourceRows = useMemo(() => {
    const grouped = new Map<string, { count: number; revenue: number }>();
    state.attendees.forEach((attendee) => {
      const key = attendee.attribution.source || "Sin atribución";
      const current = grouped.get(key) || { count: 0, revenue: 0 };
      current.count += 1;
      current.revenue += state.orders
        .filter((order) => order.attendeeId === attendee.id)
        .reduce((sum, order) => sum + getOrderNetRevenue(state, order), 0);
      grouped.set(key, current);
    });
    return Array.from(grouped, ([source, values]) => ({
      source,
      ...values,
    })).sort((a, b) => b.count - a.count);
  }, [state.attendees, state.orders]);

  const saveAttendee = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttendeeSaveError("");
    const data = new FormData(event.currentTarget);
    const existing = attendeeModal !== "new" ? attendeeModal : null;
    const requestedTicketTypeId = String(data.get("ticketTypeId") || "");
    if (existing && requestedTicketTypeId !== (existing.ticketTypeId || "")) {
      setAttendeeSaveError(
        "Para proteger órdenes y cupos, el ticket de una inscripción existente no se cambia desde el perfil.",
      );
      return;
    }
    const ticket =
      existing && requestedTicketTypeId === existing.ticketTypeId
        ? state.ticketTypes.find((item) => item.id === requestedTicketTypeId)
        : resolveTicketType(state.ticketTypes, requestedTicketTypeId);
    const ticketTypeId = ticket?.id || "";
    if (requestedTicketTypeId && !ticket) {
      setAttendeeSaveError(
        "El ticket seleccionado no está disponible por fecha, cupo o estado.",
      );
      return;
    }
    const id = existing?.id || createEventEntityId("att");
    const activity = createActivityLog(
      "attendee",
      id,
      existing ? "updated" : "created",
      existing
        ? "Perfil del inscripto actualizado."
        : "Inscripto agregado manualmente.",
    );
    const firstName = String(data.get("firstName") || "").trim();
    const lastName = String(data.get("lastName") || "").trim();
    const email = String(data.get("email") || "")
      .trim()
      .toLowerCase();
    if (
      state.attendees.some(
        (item) => item.id !== id && item.email.toLowerCase() === email,
      )
    ) {
      setAttendeeSaveError("Ya existe una inscripción con ese email.");
      return;
    }
    const price = ticket?.price ?? existing?.price ?? 0;
    const couponCode = String(data.get("couponCode") || "")
      .trim()
      .toUpperCase();
    const coupon =
      !existing && couponCode
        ? state.coupons.find((item) => item.code.toUpperCase() === couponCode)
        : undefined;
    const couponCalculation =
      coupon && ticket
        ? calculateCouponDiscount(coupon, ticket, price)
        : undefined;
    if (
      !existing &&
      couponCode &&
      (!coupon || !ticket || !couponCalculation?.valid)
    ) {
      setAttendeeSaveError(
        !coupon
          ? "El cupón no existe."
          : !ticket
            ? "Asigna un ticket antes de aplicar el cupón."
            : couponCalculation?.message || "El cupón no es válido.",
      );
      return;
    }
    const discount = couponCalculation?.discount || 0;
    const orderTotal = Math.max(0, price - discount);
    const attendee: Attendee = {
      id,
      eventId: state.event.id,
      leadOpportunityId: existing?.leadOpportunityId,
      firstName,
      lastName,
      email,
      whatsapp: String(data.get("whatsapp") || "").trim(),
      country: String(data.get("country") || "").trim(),
      profession: String(data.get("profession") || "").trim(),
      institution: String(data.get("institution") || "").trim(),
      specialty: String(data.get("specialty") || "").trim(),
      ticketTypeId: ticketTypeId || undefined,
      price,
      currency: ticket?.currency || state.event.defaultCurrency,
      paymentStatus:
        existing?.paymentStatus || (orderTotal > 0 ? "pending" : "paid"),
      registrationStatus: existing
        ? (String(
            data.get("registrationStatus") || existing.registrationStatus,
          ) as AttendeeStatus)
        : ticket?.requiresApproval
          ? "under_review"
          : orderTotal > 0
            ? "payment_pending"
            : "registered",
      scholarshipApplicationId: existing?.scholarshipApplicationId,
      attribution: {
        ...existing?.attribution,
        source: String(data.get("source") || "Alta manual"),
        campaignId: String(data.get("campaignId") || "") || undefined,
        code: couponCode || existing?.attribution.code,
      },
      registeredAt: existing?.registeredAt || new Date().toISOString(),
      uniqueCode:
        existing?.uniqueCode || createAttendeeCode(firstName, lastName),
      qrValue: existing?.qrValue || `grosym:${state.event.id}:${id}`,
      checkInStatus: existing?.checkInStatus || "not_checked_in",
      tags: String(data.get("tags") || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      notes: String(data.get("notes") || "").trim(),
      activityLogIds: [activity.id, ...(existing?.activityLogIds || [])],
    };
    const attendees = existing
      ? state.attendees.map((item) => (item.id === id ? attendee : item))
      : [attendee, ...state.attendees];
    const activityLogs = [activity, ...state.activityLogs];
    let orders = state.orders;
    let ticketTypes = state.ticketTypes;
    let coupons = state.coupons;
    if (!existing && ticket) {
      orders = [
        {
          id: createEventEntityId("order"),
          eventId: state.event.id,
          attendeeId: id,
          ticketTypeId: ticket.id,
          subtotal: price,
          discount,
          taxes: 0,
          total: orderTotal,
          currency: ticket.currency,
          status: orderTotal > 0 ? ("pending" as const) : ("paid" as const),
          createdAt: new Date().toISOString(),
        },
        ...orders,
      ];
      ticketTypes = ticketTypes.map((item) =>
        item.id === ticket.id ? { ...item, sold: item.sold + 1 } : item,
      );
      if (coupon)
        coupons = coupons.map((item) =>
          item.id === coupon.id ? { ...item, used: item.used + 1 } : item,
        );
    }
    commit({ attendees, activityLogs, orders, ticketTypes, coupons });
    setAttendeeModal(null);
  };

  const applyBulkRegistrationStatus = (registrationStatus: AttendeeStatus) => {
    if (selectedAttendeeIds.length === 0) return;
    const selected = new Set(selectedAttendeeIds);
    const now = new Date().toISOString();
    const logs = state.attendees
      .filter(
        (attendee) =>
          selected.has(attendee.id) &&
          attendee.registrationStatus !== registrationStatus,
      )
      .map((attendee) =>
        createActivityLog(
          "attendee",
          attendee.id,
          "bulk_status_changed",
          `Estado cambiado a ${ATTENDEE_LABELS[registrationStatus]} mediante acción masiva.`,
          now,
        ),
      );
    const logByAttendee = new Map(logs.map((log) => [log.entityId, log.id]));
    commit({
      attendees: state.attendees.map((attendee) => {
        const logId = logByAttendee.get(attendee.id);
        return logId
          ? {
              ...attendee,
              registrationStatus,
              activityLogIds: [logId, ...attendee.activityLogIds],
            }
          : attendee;
      }),
      activityLogs: [...logs, ...state.activityLogs],
    });
    setSelectedAttendeeIds([]);
  };

  const saveTicket = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const existing = ticketModal !== "new" ? ticketModal : null;
    const ticket: TicketType = {
      id: existing?.id || createEventEntityId("ticket"),
      eventId: state.event.id,
      name: String(data.get("name") || "").trim(),
      description: String(data.get("description") || "").trim(),
      price: Number(data.get("price") || 0),
      currency: String(
        data.get("currency") || state.event.defaultCurrency,
      ) as TicketType["currency"],
      capacity: Number(data.get("capacity") || 0),
      sold: existing?.sold || 0,
      saleStartsAt: String(data.get("saleStartsAt") || ""),
      saleEndsAt: String(data.get("saleEndsAt") || ""),
      visibility: String(
        data.get("visibility") || "public",
      ) as TicketType["visibility"],
      accessCode: String(data.get("accessCode") || "") || undefined,
      requiresApproval: data.get("requiresApproval") === "on",
      benefits: String(data.get("benefits") || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      requirements: String(data.get("requirements") || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      active: data.get("active") === "on",
      nextTicketTypeId: String(data.get("nextTicketTypeId") || "") || undefined,
    };
    const activity = createActivityLog(
      "ticket",
      ticket.id,
      existing ? "updated" : "created",
      `${existing ? "Ticket actualizado" : "Ticket creado"}: ${ticket.name}.`,
    );
    commit({
      ticketTypes: existing
        ? state.ticketTypes.map((item) =>
            item.id === ticket.id ? ticket : item,
          )
        : [...state.ticketTypes, ticket],
      activityLogs: [activity, ...state.activityLogs],
    });
    setTicketModal(null);
  };

  const saveSpeaker = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const existing = speakerModal !== "new" ? speakerModal : null;
    const photoUrl = String(data.get("photoUrl") || "").trim();
    const bio = String(data.get("bio") || "").trim();
    const audiovisualRelease = data.get("audiovisualRelease") === "on";
    const pendingAssets: Speaker["pendingAssets"] = [];
    if (!photoUrl) pendingAssets.push("photo");
    if (!bio) pendingAssets.push("bio");
    if (!audiovisualRelease) pendingAssets.push("av_release");
    if (existing?.pendingAssets.includes("talk_title"))
      pendingAssets.push("talk_title");
    if (existing?.pendingAssets.includes("presentation"))
      pendingAssets.push("presentation");
    if (existing?.pendingAssets.includes("publication"))
      pendingAssets.push("publication");
    const speaker: Speaker = {
      id: existing?.id || createEventEntityId("speaker"),
      eventId: state.event.id,
      firstName: String(data.get("firstName") || "").trim(),
      lastName: String(data.get("lastName") || "").trim(),
      photoUrl: photoUrl || undefined,
      bio: bio || undefined,
      role: String(data.get("role") || "").trim(),
      institution: String(data.get("institution") || "").trim(),
      country: String(data.get("country") || "").trim(),
      specialty: String(data.get("specialty") || "").trim(),
      linkedinUrl: String(data.get("linkedinUrl") || "") || undefined,
      instagramUrl: existing?.instagramUrl,
      email: String(data.get("email") || "").trim(),
      whatsapp: String(data.get("whatsapp") || "") || undefined,
      status: String(data.get("status") || "invited") as Speaker["status"],
      notes: String(data.get("notes") || ""),
      pendingAssets,
      audiovisualRelease,
    };
    const activity = createActivityLog(
      "speaker",
      speaker.id,
      existing ? "updated" : "created",
      `${existing ? "Speaker actualizado" : "Speaker creado"}: ${speaker.firstName} ${speaker.lastName}.`,
    );
    commit({
      speakers: existing
        ? state.speakers.map((item) =>
            item.id === speaker.id ? speaker : item,
          )
        : [...state.speakers, speaker],
      activityLogs: [activity, ...state.activityLogs],
    });
    setSpeakerModal(null);
  };

  const saveSession = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const existing = sessionModal !== "new" ? sessionModal : null;
    const speakerId = String(data.get("speakerId") || "");
    const moderatorId = String(data.get("moderatorId") || "");
    const session: EventSession = {
      id: existing?.id || createEventEntityId("session"),
      eventId: state.event.id,
      title: String(data.get("title") || "").trim(),
      description: String(data.get("description") || "").trim(),
      date: String(data.get("date") || ""),
      startsAt: String(data.get("startsAt") || ""),
      endsAt: String(data.get("endsAt") || ""),
      roomId: String(data.get("roomId") || ""),
      trackId: String(data.get("trackId") || ""),
      type: String(data.get("type") || "conference") as EventSession["type"],
      speakerIds: speakerId ? [speakerId] : [],
      moderatorIds: moderatorId ? [moderatorId] : [],
      capacity: Number(data.get("capacity") || 0),
      requiresAdditionalRegistration:
        data.get("requiresAdditionalRegistration") === "on",
      assetUrls: existing?.assetUrls || [],
      sponsorId: String(data.get("sponsorId") || "") || undefined,
      status: String(data.get("status") || "draft") as EventSession["status"],
    };
    const activity = createActivityLog(
      "session",
      session.id,
      existing ? "updated" : "created",
      `${existing ? "Sesión actualizada" : "Sesión creada"}: ${session.title}.`,
    );
    commit({
      sessions: existing
        ? state.sessions.map((item) =>
            item.id === session.id ? session : item,
          )
        : [...state.sessions, session],
      activityLogs: [activity, ...state.activityLogs],
    });
    setSessionModal(null);
  };

  const updateScholarshipStatus = (
    applicationId: string,
    status: ScholarshipStatus,
  ) => {
    const application = state.scholarships.find(
      (item) => item.id === applicationId,
    );
    if (!application || application.status === status) return;
    const activity = createActivityLog(
      "scholarship",
      application.id,
      "status_changed",
      `Beca actualizada a ${SCHOLARSHIP_LABELS[status]}.`,
    );
    commit({
      scholarships: state.scholarships.map((item) =>
        item.id === application.id ? { ...item, status } : item,
      ),
      activityLogs: [activity, ...state.activityLogs],
    });
  };

  const exportAttendees = () => {
    const headers = [
      "id",
      "nombre",
      "apellido",
      "email",
      "whatsapp",
      "pais",
      "profesion",
      "institucion",
      "especialidad",
      "ticket",
      "pago",
      "inscripcion",
      "fuente",
      "campana",
      "codigo",
    ];
    const rows = filteredAttendees.map((attendee) => [
      attendee.id,
      attendee.firstName,
      attendee.lastName,
      attendee.email,
      attendee.whatsapp || "",
      attendee.country,
      attendee.profession,
      attendee.institution || "",
      attendee.specialty || "",
      state.ticketTypes.find((ticket) => ticket.id === attendee.ticketTypeId)
        ?.name || "",
      PAYMENT_LABELS[attendee.paymentStatus],
      ATTENDEE_LABELS[attendee.registrationStatus],
      attendee.attribution.source,
      attendee.attribution.campaignId || "",
      attendee.uniqueCode,
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `inscriptos-${state.event.id}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const navItems: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
    internal?: InternalView;
    external?: ExternalView;
  }> = [
    {
      id: "overview",
      label: "Overview",
      icon: <LayoutDashboard size={14} />,
      internal: "overview",
    },
    {
      id: "convocation",
      label: "Convocatoria",
      icon: <Megaphone size={14} />,
      internal: "convocation",
    },
    {
      id: "attendees",
      label: "Inscriptos",
      icon: <Users size={14} />,
      internal: "attendees",
    },
    {
      id: "tickets",
      label: "Entradas & Pagos",
      icon: <Ticket size={14} />,
      internal: "tickets",
    },
    {
      id: "scholarships",
      label: "Becas",
      icon: <GraduationCap size={14} />,
      internal: "scholarships",
    },
    {
      id: "speakers",
      label: "Speakers",
      icon: <Mic2 size={14} />,
      internal: "speakers",
    },
    {
      id: "agenda",
      label: "Agenda",
      icon: <CalendarDays size={14} />,
      internal: "agenda",
    },
    {
      id: "communications",
      label: "Comunicaciones",
      icon: <MessageCircle size={14} />,
      internal: "communications",
    },
  ];

  const kpiCards = [
    {
      label: "Total inscriptos",
      value: metrics.totalAttendees.toString(),
      detail: `${metrics.registeredAttendees} confirmados`,
      icon: <Users size={18} />,
      tone: "text-blue-700 bg-blue-50",
    },
    {
      label: "Revenue confirmado",
      value: formatMoney(metrics.confirmedRevenue),
      detail: `${formatMoney(metrics.pendingRevenue)} pendiente`,
      icon: <CircleDollarSign size={18} />,
      tone: "text-emerald-700 bg-emerald-50",
    },
    {
      label: "Ocupación",
      value: `${metrics.occupancyRate.toFixed(1)}%`,
      detail: `${metrics.registeredAttendees} de ${state.event.capacity}`,
      icon: <Activity size={18} />,
      tone: "text-violet-700 bg-violet-50",
    },
    {
      label: "Check-in rate",
      value: `${metrics.checkInRate.toFixed(1)}%`,
      detail: `${state.checkIns.length} registros`,
      icon: <CheckCircle2 size={18} />,
      tone: "text-teal-700 bg-teal-50",
    },
  ];

  return (
    <div
      className="flex-1 min-h-0 overflow-y-auto bg-slate-50"
      data-screen-label="Event Growth Operating System"
    >
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {state.dataSource === "demo" && (
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                  Datos demo · sin pasarela conectada
                </span>
              )}
            </div>
            <h1 className="font-display text-xl sm:text-2xl font-black text-slate-950 truncate">
              {state.event.name}
            </h1>
            <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                {state.event.startDate} → {state.event.endDate}
              </span>
              <span>
                {state.event.venue}, {state.event.city}
              </span>
              <span className="font-bold text-emerald-700">
                {state.event.status}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setAttendeeModal("new")}
              className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-black text-xs flex items-center gap-2"
            >
              <Plus size={15} /> Alta manual
            </button>
            <button
              type="button"
              onClick={() => setRegistrationPortalOpen(true)}
              className="h-10 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs flex items-center gap-2 shadow-xs"
            >
              <ClipboardCheck size={16} /> Abrir registro
            </button>
          </div>
        </div>

        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 pb-3">
          <label className="lg:hidden block">
            <span className="sr-only">Módulo del evento</span>
            <select
              value={activeView}
              onChange={(event) => {
                const item = navItems.find(
                  (candidate) => candidate.id === event.target.value,
                );
                if (item?.internal) setActiveView(item.internal);
                if (item?.external) onNavigate(item.external);
              }}
              className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 font-bold text-xs"
            >
              {navItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                  {item.external ? " ↗" : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="hidden lg:flex items-center gap-1.5 overflow-x-auto mobile-scrollbar-none">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  item.internal
                    ? setActiveView(item.internal)
                    : item.external && onNavigate(item.external)
                }
                className={`shrink-0 h-9 px-3 rounded-xl border text-[11px] font-black flex items-center gap-1.5 transition-all ${item.internal === activeView ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-slate-950"}`}
              >
                {item.icon}
                {item.label}
                {item.external && <ArrowRight size={11} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-5">
        {activeView === "overview" && (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {kpiCards.map((card) => (
                <div
                  key={card.label}
                  className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-black text-slate-500">
                        {card.label}
                      </p>
                      <p className="font-display text-xl sm:text-2xl font-black text-slate-950 mt-2 tabular-nums">
                        {card.value}
                      </p>
                    </div>
                    <span
                      className={`w-9 h-9 rounded-xl flex items-center justify-center ${card.tone}`}
                    >
                      {card.icon}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    {card.detail}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-[1.35fr_1fr] gap-4">
              <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h2 className="font-display font-black text-sm text-slate-950">
                      Convocatoria → Revenue
                    </h2>
                    <p className="text-[11px] text-slate-500">
                      Atribución conservada por fuente de adquisición.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveView("convocation")}
                    className="text-[11px] font-black text-emerald-700"
                  >
                    Ver journey
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {sourceRows.map((row) => (
                    <div
                      key={row.source}
                      className="px-4 py-3 grid grid-cols-[1fr_auto_auto] items-center gap-4 text-xs"
                    >
                      <span className="font-bold text-slate-800">
                        {row.source}
                      </span>
                      <span className="font-mono font-black text-slate-950">
                        {row.count}
                      </span>
                      <span className="font-mono font-black text-emerald-700 min-w-20 text-right">
                        {formatMoney(row.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
              <section className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
                <h2 className="font-display font-black text-sm text-slate-950">
                  Alertas operativas
                </h2>
                <div className="mt-3 space-y-2">
                  {[
                    [
                      `${state.attendees.filter((item) => item.paymentStatus === "pending" || item.paymentStatus === "processing").length} pagos pendientes`,
                      "tickets" as InternalView,
                    ],
                    [
                      `${metrics.incompleteSpeakers} speakers incompletos`,
                      "speakers" as InternalView,
                    ],
                    [
                      `${metrics.scholarshipsPending} becas por revisar`,
                      "scholarships" as InternalView,
                    ],
                    [
                      `${metrics.sessionsWithoutSpeakers} sesiones sin speaker`,
                      "agenda" as InternalView,
                    ],
                  ].map(([label, view]) => (
                    <button
                      key={label}
                      onClick={() => setActiveView(view)}
                      className="w-full p-3 rounded-xl bg-amber-50/70 border border-amber-200 text-left flex items-center justify-between gap-3"
                    >
                      <span className="text-xs font-bold text-amber-950 flex items-center gap-2">
                        <AlertTriangle size={14} />
                        {label}
                      </span>
                      <ArrowRight size={14} />
                    </button>
                  ))}
                </div>
              </section>
            </div>

            <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-display font-black text-sm text-slate-950">
                    Actividad reciente
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    Cambios operativos registrados con entidad, acción y fecha.
                  </p>
                </div>
                <span className="text-[10px] font-black text-slate-500">
                  {state.activityLogs.length} eventos
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {state.activityLogs.slice(0, 8).map((log) => (
                  <div
                    key={log.id}
                    className="px-4 py-3 grid sm:grid-cols-[130px_1fr_auto] gap-2 sm:gap-4 items-center"
                  >
                    <span className="text-[9px] font-black uppercase text-emerald-700">
                      {log.entityType.replaceAll("_", " ")}
                    </span>
                    <div>
                      <p className="text-xs font-bold text-slate-800">
                        {log.description}
                      </p>
                      <p className="text-[9px] uppercase tracking-wide text-slate-400 mt-0.5">
                        {log.action.replaceAll("_", " ")}
                      </p>
                    </div>
                    <time className="text-[9px] font-mono text-slate-400">
                      {log.createdAt.slice(0, 16).replace("T", " ")}
                    </time>
                  </div>
                ))}
                {state.activityLogs.length === 0 && (
                  <p className="p-5 text-xs text-slate-500 text-center">
                    La actividad aparecerá cuando el equipo opere el evento.
                  </p>
                )}
              </div>
            </section>
          </>
        )}

        {activeView === "convocation" && (
          <div className="grid lg:grid-cols-[1fr_320px] gap-4">
            <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-display font-black text-slate-950">
                  Journey de convocatoria
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Cada registro mantiene la fuente, campaña, contenido, speaker,
                  sponsor y código que lo originó.
                </p>
              </div>
              <div className="p-4 grid sm:grid-cols-4 gap-3">
                {[
                  [
                    "Leads / interesados",
                    state.attendees.filter((item) => item.leadOpportunityId)
                      .length,
                  ],
                  ["Inscripciones iniciadas", state.attendees.length],
                  ["Órdenes creadas", state.orders.length],
                  [
                    "Pagos confirmados",
                    state.orders.filter(
                      (item) =>
                        item.status === "paid" ||
                        item.status === "partially_refunded",
                    ).length,
                  ],
                ].map(([label, value], index) => (
                  <div
                    key={String(label)}
                    className="relative rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <span className="text-[9px] font-black text-emerald-700">
                      PASO {index + 1}
                    </span>
                    <p className="text-xs font-bold text-slate-600 mt-1">
                      {label}
                    </p>
                    <p className="font-display text-2xl font-black mt-3">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4 space-y-2">
                {sourceRows.map((row) => (
                  <div key={row.source} className="flex items-center gap-3">
                    <span className="w-36 text-xs font-bold text-slate-700 truncate">
                      {row.source}
                    </span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{
                          width: `${Math.max(8, (row.count / Math.max(1, state.attendees.length)) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className="w-8 text-right text-xs font-black">
                      {row.count}
                    </span>
                  </div>
                ))}
              </div>
            </section>
            <aside className="space-y-3">
              <button
                onClick={() => onNavigate("crm")}
                className="w-full p-4 bg-blue-600 text-white rounded-2xl text-left"
              >
                <Megaphone size={20} />
                <p className="font-display font-black mt-3">Abrir CRM</p>
                <p className="text-xs text-blue-100 mt-1">
                  Gestionar leads y oportunidades.
                </p>
              </button>
              <button
                onClick={() => onNavigate("canvas")}
                className="w-full p-4 bg-slate-950 text-white rounded-2xl text-left"
              >
                <Activity size={20} className="text-emerald-400" />
                <p className="font-display font-black mt-3">Abrir journey</p>
                <p className="text-xs text-slate-300 mt-1">
                  Ver convocatoria dentro del embudo.
                </p>
              </button>
            </aside>
          </div>
        )}

        {activeView === "attendees" && (
          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
            <div className="p-3 border-b border-slate-200 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[minmax(240px,2fr)_repeat(7,minmax(96px,1fr))] gap-2">
                <div className="relative min-w-0">
                  <Search
                    size={15}
                    className="absolute left-3 top-3 text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar nombre, email, institución…"
                    className={`${inputClass} pl-9`}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as typeof statusFilter)
                  }
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">Todos los estados</option>
                  {Object.entries(ATTENDEE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <select
                  value={countryFilter}
                  onChange={(event) => setCountryFilter(event.target.value)}
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">País</option>
                  {countries.map((country) => (
                    <option key={country}>{country}</option>
                  ))}
                </select>
                <select
                  value={ticketFilter}
                  onChange={(event) => setTicketFilter(event.target.value)}
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">Ticket</option>
                  {state.ticketTypes.map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.name}
                    </option>
                  ))}
                </select>
                <select
                  value={paymentFilter}
                  onChange={(event) =>
                    setPaymentFilter(event.target.value as typeof paymentFilter)
                  }
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">Pago</option>
                  {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <select
                  value={professionFilter}
                  onChange={(event) => setProfessionFilter(event.target.value)}
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">Profesión</option>
                  {professions.map((profession) => (
                    <option key={profession}>{profession}</option>
                  ))}
                </select>
                <select
                  value={sourceFilter}
                  onChange={(event) => setSourceFilter(event.target.value)}
                  className={`${inputClass} min-w-0`}
                >
                  <option value="all">Fuente</option>
                  {sources.map((source) => (
                    <option key={source}>{source}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(event) =>
                    setSortBy(event.target.value as typeof sortBy)
                  }
                  className={`${inputClass} min-w-0`}
                >
                  <option value="registered_desc">Más recientes</option>
                  <option value="name_asc">Nombre A–Z</option>
                  <option value="country_asc">País A–Z</option>
                </select>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setFormBuilderOpen(true)}
                  className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-black text-xs flex items-center justify-center gap-2"
                >
                  <FileInput size={15} /> Formulario
                </button>
                <button
                  type="button"
                  onClick={() => setCheckInModalOpen(true)}
                  className="h-9 px-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 font-black text-xs flex items-center justify-center gap-2"
                >
                  <QrCode size={15} /> Check-in
                </button>
                <button
                  type="button"
                  onClick={() => setImportModalOpen(true)}
                  className="h-9 px-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-800 font-black text-xs flex items-center justify-center gap-2"
                >
                  <Upload size={15} /> Importar
                </button>
                <button
                  type="button"
                  onClick={exportAttendees}
                  className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-black text-xs flex items-center justify-center gap-2"
                >
                  <Download size={15} /> CSV
                </button>
              </div>
            </div>
            {selectedAttendeeIds.length > 0 && (
              <div className="px-3 py-2 border-b border-emerald-200 bg-emerald-50 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-black text-emerald-900">
                  {selectedAttendeeIds.length} inscriptos seleccionados
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => applyBulkRegistrationStatus("registered")}
                    className="h-8 px-3 rounded-lg bg-emerald-500 text-[10px] font-black"
                  >
                    Confirmar inscripción
                  </button>
                  <button
                    onClick={() =>
                      applyBulkRegistrationStatus("payment_pending")
                    }
                    className="h-8 px-3 rounded-lg border border-amber-300 bg-white text-[10px] font-black"
                  >
                    Marcar pago pendiente
                  </button>
                  <button
                    onClick={() => setSelectedAttendeeIds([])}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[10px] font-black"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[1050px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-[9px] font-black uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="pl-4 pr-1 py-3">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todos"
                        checked={
                          filteredAttendees.length > 0 &&
                          filteredAttendees.every((item) =>
                            selectedAttendeeIds.includes(item.id),
                          )
                        }
                        onChange={(event) =>
                          setSelectedAttendeeIds(
                            event.target.checked
                              ? filteredAttendees.map((item) => item.id)
                              : [],
                          )
                        }
                      />
                    </th>
                    <th className="px-3 py-3">Inscripto</th>
                    <th className="px-3 py-3">País / Perfil</th>
                    <th className="px-3 py-3">Ticket</th>
                    <th className="px-3 py-3">Pago</th>
                    <th className="px-3 py-3">Inscripción</th>
                    <th className="px-3 py-3">Atribución</th>
                    <th className="px-3 py-3">Check-in</th>
                    <th className="px-4 py-3 text-right">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAttendees.map((attendee) => {
                    const ticket = state.ticketTypes.find(
                      (item) => item.id === attendee.ticketTypeId,
                    );
                    return (
                      <tr
                        key={attendee.id}
                        onClick={() => setAttendeeModal(attendee)}
                        className="hover:bg-emerald-50/30 cursor-pointer text-xs"
                      >
                        <td className="pl-4 pr-1 py-3">
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar a ${attendee.firstName} ${attendee.lastName}`}
                            checked={selectedAttendeeIds.includes(attendee.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) =>
                              setSelectedAttendeeIds((current) =>
                                event.target.checked
                                  ? [...current, attendee.id]
                                  : current.filter((id) => id !== attendee.id),
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-black text-slate-950">
                            {attendee.firstName} {attendee.lastName}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {attendee.email}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-bold">{attendee.country}</p>
                          <p className="text-[10px] text-slate-500">
                            {attendee.profession}
                            {attendee.specialty
                              ? ` · ${attendee.specialty}`
                              : ""}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-black">
                            {ticket?.name || "Sin asignar"}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {formatMoney(attendee.price, attendee.currency)}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex px-2 py-1 rounded-lg border text-[9px] font-black ${statusTone(attendee.paymentStatus)}`}
                          >
                            {PAYMENT_LABELS[attendee.paymentStatus]}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex px-2 py-1 rounded-lg border text-[9px] font-black ${statusTone(attendee.registrationStatus)}`}
                          >
                            {ATTENDEE_LABELS[attendee.registrationStatus]}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-bold">
                            {attendee.attribution.source}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {attendee.attribution.code ||
                              attendee.attribution.campaignId ||
                              "Directo"}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`text-[10px] font-black ${attendee.checkInStatus === "checked_in" ? "text-emerald-700" : "text-slate-400"}`}
                          >
                            {attendee.checkInStatus === "checked_in"
                              ? "✓ Checked-in"
                              : "Pendiente"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-[10px] font-mono text-slate-500">
                          {attendee.registeredAt.slice(0, 10)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredAttendees.length === 0 && (
              <div className="py-16 text-center">
                <Users size={28} className="mx-auto text-slate-300" />
                <p className="font-black text-slate-700 mt-3">
                  No hay inscriptos con estos filtros
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Limpia los filtros o agrega un registro manual.
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === "tickets" && state.ticketTypes.length === 0 && (
          <EmptyState
            icon={<Ticket size={20} />}
            title="Aún no hay tipos de entrada"
            detail="Crea una entrada gratuita o paga para abrir el registro."
          />
        )}
        {activeView === "tickets" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                [
                  "Revenue total",
                  metrics.confirmedRevenue + metrics.pendingRevenue,
                ],
                ["Confirmado", metrics.confirmedRevenue],
                ["Pendiente", metrics.pendingRevenue],
                ["Reembolsado", metrics.refundedRevenue],
                ["Ticket promedio", metrics.averageTicket],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  className="bg-white border border-slate-200 rounded-2xl p-4"
                >
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                    {label}
                  </p>
                  <p className="font-display text-lg font-black mt-2">
                    {formatMoney(Number(value))}
                  </p>
                </div>
              ))}
            </div>
            <section>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-display font-black text-slate-950">
                    Tipos de entrada
                  </h2>
                  <p className="text-xs text-slate-500">
                    Gratuitas o pagas, públicas u ocultas y con períodos de
                    venta.
                  </p>
                </div>
                <button
                  onClick={() => setTicketModal("new")}
                  className="h-10 px-3 rounded-xl bg-slate-950 text-white text-xs font-black flex items-center gap-2"
                >
                  <Plus size={15} /> Nuevo ticket
                </button>
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {state.ticketTypes.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => setTicketModal(ticket)}
                    className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-emerald-400 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span
                          className={`text-[9px] font-black uppercase tracking-wider ${ticket.active ? "text-emerald-700" : "text-slate-400"}`}
                        >
                          {ticket.active ? "Activo" : "Inactivo"} ·{" "}
                          {ticket.visibility === "hidden"
                            ? "Oculto"
                            : "Público"}
                        </span>
                        <h3 className="font-display font-black text-slate-950 mt-1">
                          {ticket.name}
                        </h3>
                      </div>
                      <span className="font-display text-xl font-black">
                        {ticket.price === 0
                          ? "Gratis"
                          : formatMoney(ticket.price, ticket.currency)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                      {ticket.description}
                    </p>
                    <div className="mt-4">
                      <div className="flex justify-between text-[10px] font-bold">
                        <span>{ticket.sold} vendidos</span>
                        <span>{ticket.capacity} cupos</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500"
                          style={{
                            width: `${Math.min(100, (ticket.sold / Math.max(1, ticket.capacity)) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {ticket.requiresApproval && (
                        <span className="text-[9px] font-bold px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                          Requiere aprobación
                        </span>
                      )}
                      {ticket.accessCode && (
                        <span className="text-[9px] font-mono font-bold px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg">
                          Código: {ticket.accessCode}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
            <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-display font-black text-slate-950">
                    Órdenes & pagos
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Capa desacoplada: Stripe y Mercado Pago aún no están
                    conectados.
                  </p>
                </div>
                <span className="text-[9px] font-black uppercase px-2 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
                  No procesar como real
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-xs">
                  <thead className="bg-slate-50 text-left text-[9px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Orden</th>
                      <th className="px-3 py-3">Inscripto</th>
                      <th className="px-3 py-3">Ticket</th>
                      <th className="px-3 py-3">Gateway</th>
                      <th className="px-3 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {state.orders.map((order) => {
                      const attendee = state.attendees.find(
                        (item) => item.id === order.attendeeId,
                      );
                      const ticket = state.ticketTypes.find(
                        (item) => item.id === order.ticketTypeId,
                      );
                      const payment = state.payments.find(
                        (item) => item.orderId === order.id,
                      );
                      return (
                        <tr key={order.id}>
                          <td className="px-4 py-3 font-mono text-[10px]">
                            {order.id}
                          </td>
                          <td className="px-3 py-3 font-bold">
                            {attendee
                              ? `${attendee.firstName} ${attendee.lastName}`
                              : "Sin relación"}
                          </td>
                          <td className="px-3 py-3">{ticket?.name}</td>
                          <td className="px-3 py-3 text-slate-500">
                            {payment?.gateway || "sin pago"}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`px-2 py-1 rounded-lg border text-[9px] font-black ${statusTone(order.status)}`}
                            >
                              {PAYMENT_LABELS[order.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-black">
                            {formatMoney(order.total, order.currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <div className="grid xl:grid-cols-3 gap-3">
              {(
                [
                  ["Por ticket", financialBreakdowns.byTicket],
                  ["Por fuente", financialBreakdowns.bySource],
                  ["Por campaña", financialBreakdowns.byCampaign],
                ] as const
              ).map(([title, rows]) => (
                <section
                  key={title}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
                >
                  <div className="p-3 border-b border-slate-200">
                    <h3 className="font-display font-black text-sm">{title}</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {rows.slice(0, 6).map((row) => (
                      <div
                        key={row.id}
                        className="p-3 grid grid-cols-[1fr_auto_auto] gap-3 items-center text-xs"
                      >
                        <span className="font-bold truncate">{row.label}</span>
                        <span className="text-slate-500">{row.orders}</span>
                        <span className="font-mono font-black text-emerald-700">
                          {formatMoney(row.revenue)}
                        </span>
                      </div>
                    ))}
                    {rows.length === 0 && (
                      <p className="p-5 text-xs text-slate-500 text-center">
                        Sin ventas confirmadas.
                      </p>
                    )}
                  </div>
                </section>
              ))}
            </div>
            <EventCouponManager
              state={state}
              sponsors={sponsors}
              onChange={onChange}
            />
          </>
        )}

        {activeView === "scholarships" && state.scholarships.length === 0 && (
          <EmptyState
            icon={<GraduationCap size={20} />}
            title="No hay solicitudes de beca"
            detail="Las solicitudes aparecerán aquí al llegar desde el registro."
          />
        )}
        {activeView === "scholarships" && (
          <div className="grid xl:grid-cols-[1fr_320px] gap-4">
            <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-display font-black text-slate-950">
                  Scholarship Manager
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Solicitud → revisión → beneficio → inscripción → confirmación.
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {state.scholarships.map((application) => {
                  const attendee = state.attendees.find(
                    (item) => item.id === application.attendeeId,
                  );
                  return (
                    <div
                      key={application.id}
                      className="p-4 grid md:grid-cols-[1fr_160px_170px] gap-3 items-center"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-black text-sm text-slate-950">
                            {attendee
                              ? `${attendee.firstName} ${attendee.lastName}`
                              : "Sin inscripto"}
                          </p>
                          <span
                            className={`text-[9px] font-black px-2 py-1 rounded-lg border ${statusTone(application.status)}`}
                          >
                            {SCHOLARSHIP_LABELS[application.status]}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {application.profession} · {application.country} ·{" "}
                          {application.institution || "Sin institución"}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-2 line-clamp-2">
                          {application.reason}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase text-slate-400">
                          Beneficio
                        </p>
                        <p className="font-display font-black mt-1">
                          {application.percentage
                            ? `${application.percentage}%`
                            : application.amount
                              ? formatMoney(application.amount)
                              : "Ticket especial"}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {application.sponsorId
                            ? sponsorMap.get(application.sponsorId) ||
                              "Sponsor relacionado"
                            : "Sin sponsor"}
                        </p>
                      </div>
                      <select
                        value={application.status}
                        onChange={(event) =>
                          updateScholarshipStatus(
                            application.id,
                            event.target.value as ScholarshipStatus,
                          )
                        }
                        className={inputClass}
                      >
                        {Object.entries(SCHOLARSHIP_LABELS).map(
                          ([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  );
                })}
              </div>
            </section>
            <aside className="space-y-3">
              <div className="bg-slate-950 text-white rounded-2xl p-4">
                <p className="text-[9px] font-black uppercase text-emerald-400">
                  Becas patrocinadas
                </p>
                <p className="font-display text-3xl font-black mt-2">
                  {state.scholarships.filter((item) => item.sponsorId).length}
                </p>
                <p className="text-xs text-slate-300 mt-1">
                  solicitudes vinculadas a sponsors
                </p>
                <button
                  onClick={() => onNavigate("sponsors")}
                  className="mt-4 w-full h-10 rounded-xl bg-emerald-500 text-slate-950 font-black text-xs"
                >
                  Abrir Sponsors
                </button>
              </div>
              {sponsors
                .filter((sponsor) =>
                  state.scholarships.some(
                    (item) => item.sponsorId === sponsor.id,
                  ),
                )
                .map((sponsor) => {
                  const funded = state.scholarships.filter(
                    (item) => item.sponsorId === sponsor.id,
                  );
                  return (
                    <div
                      key={sponsor.id}
                      className="bg-white border border-slate-200 rounded-2xl p-4"
                    >
                      <p className="font-black text-sm">
                        {sponsor.companyName}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-center">
                        <div className="bg-slate-50 rounded-xl p-2">
                          <p className="font-display font-black">
                            {funded.length}
                          </p>
                          <p className="text-[9px] text-slate-500">Asignadas</p>
                        </div>
                        <div className="bg-emerald-50 rounded-xl p-2">
                          <p className="font-display font-black text-emerald-700">
                            {
                              funded.filter(
                                (item) =>
                                  item.status === "used" ||
                                  item.status === "code_issued",
                              ).length
                            }
                          </p>
                          <p className="text-[9px] text-slate-500">
                            Utilizadas
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </aside>
          </div>
        )}

        {activeView === "speakers" && state.speakers.length === 0 && (
          <EmptyState
            icon={<Mic2 size={20} />}
            title="Aún no hay speakers"
            detail="Agrega el primero y luego relaciónalo con una o más sesiones."
          />
        )}
        {activeView === "speakers" && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-display font-black text-slate-950">
                  Speakers
                </h2>
                <p className="text-xs text-slate-500">
                  Una fuente de datos, conectada con múltiples actividades.
                </p>
              </div>
              <button
                onClick={() => setSpeakerModal("new")}
                className="h-10 px-3 rounded-xl bg-slate-950 text-white text-xs font-black flex items-center gap-2"
              >
                <Plus size={15} /> Nuevo speaker
              </button>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {state.speakers.map((speaker) => {
                const sessionCount = state.sessions.filter((session) =>
                  [...session.speakerIds, ...session.moderatorIds].includes(
                    speaker.id,
                  ),
                ).length;
                return (
                  <button
                    key={speaker.id}
                    onClick={() => setSpeakerModal(speaker)}
                    className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-emerald-400"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center font-display font-black text-emerald-700">
                        {speaker.photoUrl ? (
                          <img
                            src={speaker.photoUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          `${speaker.firstName.charAt(0)}${speaker.lastName.charAt(0)}`
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-display font-black text-sm text-slate-950 truncate">
                            {speaker.firstName} {speaker.lastName}
                          </h3>
                          <span
                            className={`text-[9px] font-black px-2 py-1 rounded-lg border ${statusTone(speaker.status)}`}
                          >
                            {speaker.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-1">
                          {speaker.role} · {speaker.institution}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-50 p-2">
                        <p className="font-display font-black">
                          {sessionCount}
                        </p>
                        <p className="text-[9px] text-slate-500">Actividades</p>
                      </div>
                      <div
                        className={`rounded-xl p-2 ${speaker.pendingAssets.length ? "bg-amber-50" : "bg-emerald-50"}`}
                      >
                        <p
                          className={`font-display font-black ${speaker.pendingAssets.length ? "text-amber-700" : "text-emerald-700"}`}
                        >
                          {speaker.pendingAssets.length}
                        </p>
                        <p className="text-[9px] text-slate-500">
                          Assets pendientes
                        </p>
                      </div>
                    </div>
                    {speaker.pendingAssets.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {speaker.pendingAssets.map((asset) => (
                          <span
                            key={asset}
                            className="text-[9px] px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 font-bold"
                          >
                            {asset.replace("_", " ")}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {activeView === "agenda" && state.sessions.length === 0 && (
          <EmptyState
            icon={<CalendarDays size={20} />}
            title="La agenda está vacía"
            detail="Crea una sesión y asígnale sala, track y speakers por ID."
          />
        )}
        {activeView === "agenda" && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-display font-black text-slate-950">
                  Agenda
                </h2>
                <p className="text-xs text-slate-500">
                  Día → sala → track → sesión → speakers.
                </p>
              </div>
              <button
                onClick={() => setSessionModal("new")}
                className="h-10 px-3 rounded-xl bg-slate-950 text-white text-xs font-black flex items-center gap-2"
              >
                <Plus size={15} /> Nueva sesión
              </button>
            </div>
            <div className="space-y-4">
              {Array.from(
                new Set(state.sessions.map((session) => session.date)),
              )
                .sort()
                .map((date) => (
                  <div
                    key={date}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-slate-950 text-white flex items-center gap-2">
                      <CalendarDays size={15} className="text-emerald-400" />
                      <h3 className="font-display font-black text-sm">
                        {new Date(`${date}T12:00:00`).toLocaleDateString(
                          "es-AR",
                          { weekday: "long", day: "numeric", month: "long" },
                        )}
                      </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {state.sessions
                        .filter((session) => session.date === date)
                        .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
                        .map((session) => {
                          const track = trackMap.get(session.trackId);
                          return (
                            <button
                              key={session.id}
                              onClick={() => setSessionModal(session)}
                              className="w-full p-4 grid sm:grid-cols-[100px_1fr_180px] gap-3 text-left hover:bg-slate-50"
                            >
                              <div>
                                <p className="font-display font-black text-lg">
                                  {session.startsAt}
                                </p>
                                <p className="text-[10px] text-slate-500">
                                  {session.endsAt}
                                </p>
                              </div>
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <h4 className="font-black text-sm text-slate-950">
                                    {session.title}
                                  </h4>
                                  <span
                                    className={`text-[9px] font-black px-2 py-1 rounded-lg border ${statusTone(session.status)}`}
                                  >
                                    {session.status}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {session.speakerIds.length
                                    ? session.speakerIds
                                        .map((id) => speakerMap.get(id))
                                        .filter(Boolean)
                                        .join(", ")
                                    : "Sin speaker asignado"}
                                </p>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  <span className="text-[9px] font-bold px-2 py-1 rounded-lg bg-slate-50 border border-slate-200">
                                    {roomMap.get(session.roomId)}
                                  </span>
                                  {track && (
                                    <span
                                      className="text-[9px] font-bold px-2 py-1 rounded-lg border"
                                      style={{
                                        color: track.color,
                                        borderColor: `${track.color}55`,
                                        backgroundColor: `${track.color}0f`,
                                      }}
                                    >
                                      {track.name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="sm:text-right">
                                <p className="text-xs font-bold">
                                  Cupo {session.capacity}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-1">
                                  {session.type.replace("_", " ")}
                                </p>
                                {session.sponsorId && (
                                  <p className="text-[10px] font-bold text-amber-700 mt-2">
                                    Sponsor: {sponsorMap.get(session.sponsorId)}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {activeView === "communications" && (
          <EventCommunicationsPanel
            state={state}
            onChange={onChange}
            onOpenCrm={() => onNavigate("crm")}
          />
        )}
      </div>

      <EventAttendeeImportModal
        open={importModalOpen}
        state={state}
        onClose={() => setImportModalOpen(false)}
        onImport={(result) => {
          onChange(mergeImportedAttendees(state, result.attendees));
          setImportModalOpen(false);
        }}
      />
      <EventCheckInModal
        open={checkInModalOpen}
        state={state}
        onChange={onChange}
        onClose={() => setCheckInModalOpen(false)}
      />
      <EventRegistrationPortalModal
        open={registrationPortalOpen}
        state={state}
        onChange={onChange}
        onClose={() => setRegistrationPortalOpen(false)}
      />
      {formBuilderOpen && (
        <EventRegistrationFormBuilder
          state={state}
          onChange={onChange}
          onClose={() => setFormBuilderOpen(false)}
        />
      )}

      {attendeeModal && (
        <ModalShell
          title={
            attendeeModal === "new" ? "Nuevo inscripto" : "Perfil del inscripto"
          }
          subtitle="Atribución, ticket y estado en una sola fuente."
          onClose={() => {
            setAttendeeModal(null);
            setAttendeeSaveError("");
          }}
        >
          <form onSubmit={saveAttendee} className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <input
                name="firstName"
                required
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.firstName
                }
                className={inputClass}
              />
            </Field>
            <Field label="Apellido" required>
              <input
                name="lastName"
                required
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.lastName
                }
                className={inputClass}
              />
            </Field>
            <Field label="Email" required>
              <input
                name="email"
                type="email"
                required
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.email
                }
                className={inputClass}
              />
            </Field>
            <Field label="WhatsApp">
              <input
                name="whatsapp"
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.whatsapp
                }
                className={inputClass}
              />
            </Field>
            <Field label="País" required>
              <input
                name="country"
                required
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.country
                }
                className={inputClass}
              />
            </Field>
            <Field label="Profesión" required>
              <input
                name="profession"
                required
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.profession
                }
                className={inputClass}
              />
            </Field>
            <Field label="Institución">
              <input
                name="institution"
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.institution
                }
                className={inputClass}
              />
            </Field>
            <Field label="Especialidad">
              <input
                name="specialty"
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.specialty
                }
                className={inputClass}
              />
            </Field>
            <Field label="Tipo de entrada">
              <select
                name="ticketTypeId"
                disabled={attendeeModal !== "new"}
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.ticketTypeId
                }
                className={`${inputClass} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}
              >
                <option value="">Sin asignar</option>
                {state.ticketTypes
                  .filter(
                    (ticket) =>
                      attendeeModal !== "new" ||
                      availableTicketIds.has(ticket.id),
                  )
                  .map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.name} ·{" "}
                      {ticket.price === 0
                        ? "Gratis"
                        : formatMoney(ticket.price, ticket.currency)}
                    </option>
                  ))}
              </select>
            </Field>
            {attendeeModal !== "new" ? (
              <Field label="Estado de inscripción">
                <select
                  name="registrationStatus"
                  defaultValue={attendeeModal.registrationStatus}
                  className={inputClass}
                >
                  {Object.entries(ATTENDEE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <p className="font-black">Estado automático</p>
                <p className="mt-1 text-[10px] leading-relaxed">
                  Se asigna según aprobación, precio y pago. Un ticket pago no
                  se marca como confirmado sin evidencia real.
                </p>
              </div>
            )}
            <Field label="Fuente">
              <input
                name="source"
                defaultValue={
                  attendeeModal === "new"
                    ? "Alta manual"
                    : attendeeModal.attribution.source
                }
                className={inputClass}
              />
            </Field>
            <Field label="Campaña">
              <select
                name="campaignId"
                defaultValue={
                  attendeeModal === "new"
                    ? ""
                    : attendeeModal.attribution.campaignId
                }
                className={inputClass}
              >
                <option value="">Sin campaña</option>
                {state.campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Cupón / código">
              <input
                name="couponCode"
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.attribution.code
                }
                placeholder="Opcional"
                className={inputClass}
              />
            </Field>
            <Field label="Tags">
              <input
                name="tags"
                defaultValue={
                  attendeeModal === "new" ? "" : attendeeModal.tags.join(", ")
                }
                placeholder="vip, médico, partner"
                className={inputClass}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notas">
                <input
                  name="notes"
                  defaultValue={
                    attendeeModal === "new" ? "" : attendeeModal.notes
                  }
                  className={inputClass}
                />
              </Field>
            </div>
            {attendeeSaveError && (
              <p className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-800">
                {attendeeSaveError}
              </p>
            )}
            {attendeeModal !== "new" && (
              <div className="sm:col-span-2 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <p className="text-[10px] font-black uppercase text-slate-500">
                    Historial operativo
                  </p>
                </div>
                <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {state.activityLogs
                    .filter((log) => log.entityId === attendeeModal.id)
                    .map((log) => (
                      <div
                        key={log.id}
                        className="px-3 py-2 grid grid-cols-[1fr_auto] gap-3"
                      >
                        <div>
                          <p className="text-xs font-bold">{log.description}</p>
                          <p className="text-[9px] uppercase font-black text-emerald-700 mt-0.5">
                            {log.action.replaceAll("_", " ")}
                          </p>
                        </div>
                        <span className="text-[9px] font-mono text-slate-400">
                          {log.createdAt.slice(0, 16).replace("T", " ")}
                        </span>
                      </div>
                    ))}
                  {state.activityLogs.every(
                    (log) => log.entityId !== attendeeModal.id,
                  ) && (
                    <p className="p-3 text-xs text-slate-500">
                      Todavía no hay actividad registrada.
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setAttendeeModal(null);
                  setAttendeeSaveError("");
                }}
                className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-10 px-5 rounded-xl bg-emerald-500 text-slate-950 text-xs font-black"
              >
                Guardar inscripto
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {ticketModal && (
        <ModalShell
          title={
            ticketModal === "new"
              ? "Nuevo tipo de entrada"
              : "Editar tipo de entrada"
          }
          subtitle="Configurable, gratuito o pago; no está conectado a una pasarela."
          onClose={() => setTicketModal(null)}
        >
          <form onSubmit={saveTicket} className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <input
                name="name"
                required
                defaultValue={ticketModal === "new" ? "" : ticketModal.name}
                className={inputClass}
              />
            </Field>
            <Field label="Precio">
              <input
                name="price"
                type="number"
                min="0"
                step="0.01"
                defaultValue={ticketModal === "new" ? 0 : ticketModal.price}
                className={inputClass}
              />
            </Field>
            <Field label="Descripción" required>
              <input
                name="description"
                required
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.description
                }
                className={inputClass}
              />
            </Field>
            <Field label="Moneda">
              <select
                name="currency"
                defaultValue={
                  ticketModal === "new"
                    ? state.event.defaultCurrency
                    : ticketModal.currency
                }
                className={inputClass}
              >
                {["USD", "ARS", "EUR", "BRL", "CLP", "COP", "MXN"].map(
                  (currency) => (
                    <option key={currency}>{currency}</option>
                  ),
                )}
              </select>
            </Field>
            <Field label="Cupo" required>
              <input
                name="capacity"
                type="number"
                min="0"
                required
                defaultValue={
                  ticketModal === "new" ? 100 : ticketModal.capacity
                }
                className={inputClass}
              />
            </Field>
            <Field label="Visibilidad">
              <select
                name="visibility"
                defaultValue={
                  ticketModal === "new" ? "public" : ticketModal.visibility
                }
                className={inputClass}
              >
                <option value="public">Público</option>
                <option value="hidden">Oculto</option>
              </select>
            </Field>
            <Field label="Inicio de venta">
              <input
                name="saleStartsAt"
                type="date"
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.saleStartsAt
                }
                className={inputClass}
              />
            </Field>
            <Field label="Cierre de venta">
              <input
                name="saleEndsAt"
                type="date"
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.saleEndsAt
                }
                className={inputClass}
              />
            </Field>
            <Field label="Código de acceso">
              <input
                name="accessCode"
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.accessCode
                }
                className={inputClass}
              />
            </Field>
            <Field label="Siguiente ticket automático">
              <select
                name="nextTicketTypeId"
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.nextTicketTypeId
                }
                className={inputClass}
              >
                <option value="">Ninguno</option>
                {state.ticketTypes
                  .filter(
                    (ticket) =>
                      ticket.id !==
                      (ticketModal === "new" ? "" : ticketModal.id),
                  )
                  .map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      {ticket.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Beneficios">
              <input
                name="benefits"
                defaultValue={
                  ticketModal === "new" ? "" : ticketModal.benefits.join(", ")
                }
                className={inputClass}
              />
            </Field>
            <Field label="Requisitos">
              <input
                name="requirements"
                defaultValue={
                  ticketModal === "new"
                    ? ""
                    : ticketModal.requirements.join(", ")
                }
                className={inputClass}
              />
            </Field>
            <label className="flex items-center gap-2 text-xs font-bold">
              <input
                name="requiresApproval"
                type="checkbox"
                defaultChecked={
                  ticketModal === "new" ? false : ticketModal.requiresApproval
                }
              />{" "}
              Requiere aprobación
            </label>
            <label className="flex items-center gap-2 text-xs font-bold">
              <input
                name="active"
                type="checkbox"
                defaultChecked={
                  ticketModal === "new" ? true : ticketModal.active
                }
              />{" "}
              Ticket activo
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTicketModal(null)}
                className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-10 px-5 rounded-xl bg-emerald-500 text-slate-950 text-xs font-black"
              >
                Guardar ticket
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {speakerModal && (
        <ModalShell
          title={speakerModal === "new" ? "Nuevo speaker" : "Editar speaker"}
          subtitle="Los cambios se reflejan automáticamente en sus sesiones."
          onClose={() => setSpeakerModal(null)}
        >
          <form onSubmit={saveSpeaker} className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <input
                name="firstName"
                required
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.firstName
                }
                className={inputClass}
              />
            </Field>
            <Field label="Apellido" required>
              <input
                name="lastName"
                required
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.lastName
                }
                className={inputClass}
              />
            </Field>
            <Field label="Email" required>
              <input
                name="email"
                type="email"
                required
                defaultValue={speakerModal === "new" ? "" : speakerModal.email}
                className={inputClass}
              />
            </Field>
            <Field label="WhatsApp">
              <input
                name="whatsapp"
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.whatsapp
                }
                className={inputClass}
              />
            </Field>
            <Field label="Cargo / rol" required>
              <input
                name="role"
                required
                defaultValue={speakerModal === "new" ? "" : speakerModal.role}
                className={inputClass}
              />
            </Field>
            <Field label="Institución" required>
              <input
                name="institution"
                required
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.institution
                }
                className={inputClass}
              />
            </Field>
            <Field label="País">
              <input
                name="country"
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.country
                }
                className={inputClass}
              />
            </Field>
            <Field label="Especialidad">
              <input
                name="specialty"
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.specialty
                }
                className={inputClass}
              />
            </Field>
            <Field label="Foto URL">
              <input
                name="photoUrl"
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.photoUrl
                }
                className={inputClass}
              />
            </Field>
            <Field label="LinkedIn">
              <input
                name="linkedinUrl"
                defaultValue={
                  speakerModal === "new" ? "" : speakerModal.linkedinUrl
                }
                className={inputClass}
              />
            </Field>
            <Field label="Estado">
              <select
                name="status"
                defaultValue={
                  speakerModal === "new" ? "invited" : speakerModal.status
                }
                className={inputClass}
              >
                {[
                  "invited",
                  "contacted",
                  "confirmed",
                  "info_pending",
                  "complete",
                  "published",
                  "cancelled",
                ].map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Notas">
              <input
                name="notes"
                defaultValue={speakerModal === "new" ? "" : speakerModal.notes}
                className={inputClass}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Bio">
                <textarea
                  name="bio"
                  rows={4}
                  defaultValue={speakerModal === "new" ? "" : speakerModal.bio}
                  className={`${inputClass} h-auto py-3`}
                />
              </Field>
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-xs font-bold">
              <input
                name="audiovisualRelease"
                type="checkbox"
                defaultChecked={
                  speakerModal === "new"
                    ? false
                    : speakerModal.audiovisualRelease
                }
              />{" "}
              Autorización audiovisual recibida
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSpeakerModal(null)}
                className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-10 px-5 rounded-xl bg-emerald-500 text-slate-950 text-xs font-black"
              >
                Guardar speaker
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {sessionModal && (
        <ModalShell
          title={sessionModal === "new" ? "Nueva sesión" : "Editar sesión"}
          subtitle="Relaciona speakers, sala, track y sponsor sin duplicar datos."
          onClose={() => setSessionModal(null)}
        >
          <form onSubmit={saveSession} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Título" required>
                <input
                  name="title"
                  required
                  defaultValue={
                    sessionModal === "new" ? "" : sessionModal.title
                  }
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Fecha" required>
              <input
                name="date"
                type="date"
                required
                defaultValue={
                  sessionModal === "new"
                    ? state.event.startDate
                    : sessionModal.date
                }
                className={inputClass}
              />
            </Field>
            <Field label="Tipo">
              <select
                name="type"
                defaultValue={
                  sessionModal === "new" ? "conference" : sessionModal.type
                }
                className={inputClass}
              >
                {[
                  "conference",
                  "panel",
                  "consensus",
                  "workshop",
                  "course",
                  "moderation",
                  "ceremony",
                  "networking",
                  "sponsor_session",
                ].map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Hora inicio">
              <input
                name="startsAt"
                type="time"
                defaultValue={
                  sessionModal === "new" ? "09:00" : sessionModal.startsAt
                }
                className={inputClass}
              />
            </Field>
            <Field label="Hora fin">
              <input
                name="endsAt"
                type="time"
                defaultValue={
                  sessionModal === "new" ? "10:00" : sessionModal.endsAt
                }
                className={inputClass}
              />
            </Field>
            <Field label="Sala">
              <select
                name="roomId"
                defaultValue={
                  sessionModal === "new"
                    ? state.rooms[0]?.id
                    : sessionModal.roomId
                }
                className={inputClass}
              >
                {state.rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Track">
              <select
                name="trackId"
                defaultValue={
                  sessionModal === "new"
                    ? state.tracks[0]?.id
                    : sessionModal.trackId
                }
                className={inputClass}
              >
                {state.tracks.map((track) => (
                  <option key={track.id} value={track.id}>
                    {track.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Speaker">
              <select
                name="speakerId"
                defaultValue={
                  sessionModal === "new" ? "" : sessionModal.speakerIds[0]
                }
                className={inputClass}
              >
                <option value="">Sin speaker</option>
                {state.speakers.map((speaker) => (
                  <option key={speaker.id} value={speaker.id}>
                    {speaker.firstName} {speaker.lastName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Moderador">
              <select
                name="moderatorId"
                defaultValue={
                  sessionModal === "new" ? "" : sessionModal.moderatorIds[0]
                }
                className={inputClass}
              >
                <option value="">Sin moderador</option>
                {state.speakers.map((speaker) => (
                  <option key={speaker.id} value={speaker.id}>
                    {speaker.firstName} {speaker.lastName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sponsor">
              <select
                name="sponsorId"
                defaultValue={
                  sessionModal === "new" ? "" : sessionModal.sponsorId
                }
                className={inputClass}
              >
                <option value="">Sin sponsor</option>
                {sponsors.map((sponsor) => (
                  <option key={sponsor.id} value={sponsor.id}>
                    {sponsor.companyName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Cupo">
              <input
                name="capacity"
                type="number"
                min="0"
                defaultValue={
                  sessionModal === "new" ? 100 : sessionModal.capacity
                }
                className={inputClass}
              />
            </Field>
            <Field label="Estado">
              <select
                name="status"
                defaultValue={
                  sessionModal === "new" ? "draft" : sessionModal.status
                }
                className={inputClass}
              >
                {[
                  "draft",
                  "confirmed",
                  "published",
                  "completed",
                  "cancelled",
                ].map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-xs font-bold">
              <input
                name="requiresAdditionalRegistration"
                type="checkbox"
                defaultChecked={
                  sessionModal === "new"
                    ? false
                    : sessionModal.requiresAdditionalRegistration
                }
              />{" "}
              Inscripción adicional
            </label>
            <div className="sm:col-span-2">
              <Field label="Descripción">
                <textarea
                  name="description"
                  rows={3}
                  defaultValue={
                    sessionModal === "new" ? "" : sessionModal.description
                  }
                  className={`${inputClass} h-auto py-3`}
                />
              </Field>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSessionModal(null)}
                className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-10 px-5 rounded-xl bg-emerald-500 text-slate-950 text-xs font-black"
              >
                Guardar sesión
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};
